import React, { useState, useCallback, useEffect } from 'react';

    const BI = ({ icon }) => <i className={`bi bi-${icon}`} aria-hidden="true" />;

    export function ConfirmDialog({ open, opts, onResolve }) {
    const {
      title = 'Confirmer',
      subtitle,
      message = 'Confirmer cette action ?',
      detail,
      confirmLabel = 'Confirmer',
      cancelLabel = 'Annuler',
      extraLabel,
      extraIcon = 'trash3',
      tone = 'danger',
      icon = tone === 'danger' ? 'exclamation-triangle' : 'question-circle',
      dismissOnly = false,
    } = opts || {};

    useEffect(() => {
      if (!open) return undefined;
      const precedent = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (event) => {
        if (event.key === 'Escape') { event.preventDefault(); onResolve(dismissOnly ? true : false); }
        else if (event.key === 'Enter') { event.preventDefault(); onResolve(true); }
      };
      window.addEventListener('keydown', onKey);
      return () => {
        document.body.style.overflow = precedent;
        window.removeEventListener('keydown', onKey);
      };
    }, [open, onResolve, dismissOnly]);

    if (!open) return null;

    return (
      <div className="erp-dialog-backdrop" onMouseDown={(event) => {
        if (event.target === event.currentTarget) onResolve(dismissOnly ? true : false);
      }}>
        <section className={`erp-dialog erp-dialog--${tone}`} role="dialog" aria-modal="true" aria-labelledby="erp-dialog-title">
          <header className="erp-dialog__header">
            <span className="erp-dialog__icon"><BI icon={icon} /></span>
            <div className="erp-dialog__heading">
              <h2 id="erp-dialog-title">{title}</h2>
              {subtitle && <p>{subtitle}</p>}
            </div>
            <button type="button" className="erp-dialog__close" onClick={() => onResolve(dismissOnly ? true : false)} aria-label="Fermer">
              <BI icon="x-lg" />
            </button>
          </header>

          <div className="erp-dialog__body">
            <p className="erp-dialog__message">{message}</p>
            {detail && <p className="erp-dialog__detail">{detail}</p>}
          </div>

          <footer className="erp-dialog__actions">
            {!dismissOnly && (
              <button type="button" className="erp-btn erp-btn--secondary" onClick={() => onResolve(false)}>{cancelLabel}</button>
            )}
            {extraLabel && !dismissOnly && (
              <button type="button" className="erp-btn erp-btn--danger" onClick={() => onResolve('extra')}>
                <BI icon={extraIcon} /> {extraLabel}
              </button>
            )}
            <button type="button" autoFocus className={`erp-btn ${tone === 'danger' ? 'erp-btn--danger' : 'erp-btn--primary'}`} onClick={() => onResolve(true)}>
              <BI icon={dismissOnly ? 'check-lg' : tone === 'danger' ? 'trash3' : 'check-lg'} /> {confirmLabel}
            </button>
          </footer>
        </section>
      </div>
    );
    }

    const attente = [];
    const abonnes = new Set();
    let demandeActive = null;

    const publier = () => {
    if (!demandeActive && attente.length) demandeActive = attente.shift();
    abonnes.forEach((abonne) => abonne(demandeActive));
    };

    export function confirmErpia(opts) {
    return new Promise((resolve) => {
      attente.push({ opts: typeof opts === 'string' ? { message: opts } : opts, resolve });
      publier();
    });
    }

    export function alertErpia(opts) {
    const normalise = typeof opts === 'string' ? { message: opts } : opts;
    return confirmErpia({ title: 'Information', confirmLabel: 'Fermer', tone: 'primary', ...normalise, dismissOnly: true });
    }

    const resoudreGlobal = (value) => {
    if (!demandeActive) return;
    const courante = demandeActive;
    demandeActive = null;
    courante.resolve(value);
    publier();
    };

    export function ErpiaConfirmHost() {
    const [demande, setDemande] = useState(demandeActive);
    useEffect(() => {
      abonnes.add(setDemande);
      publier();
      return () => abonnes.delete(setDemande);
    }, []);
    if (!demande) return null;
    return <ConfirmDialog open opts={demande.opts} onResolve={resoudreGlobal} />;
    }

    export function useConfirm() {
    const [state, setState] = useState({ open: false, opts: null, resolve: null });
    const confirm = useCallback((opts) => new Promise((resolve) => setState({ open: true, opts, resolve })), []);
    const onResolve = useCallback((value) => {
      setState((current) => {
        current.resolve?.(value);
        return { open: false, opts: current.opts, resolve: null };
      });
    }, []);
    return { confirm, confirmElement: <ConfirmDialog open={state.open} opts={state.opts} onResolve={onResolve} /> };
    }

    export default ConfirmDialog;
    